<script lang="ts" setup>
import type { TruckDto } from "@/api/trucks/dto/truck.dto";
import { AdvancedMarker } from "vue3-google-map";
import { computed } from "vue";
import { TruckStatus } from "@/constants/truck";
import { DriverStatusOnHold, OwnerStatus } from "@/constants/user";

const props = defineProps<{
  truck: TruckDto;
}>();

const position = computed(() => {
  if (props.truck.currentLocation?.coordinates) {
    return {
      lat: props.truck.currentLocation.coordinates.lat,
      lng: props.truck.currentLocation.coordinates.lon,
    };
  }

  return null;
});

const statusClasses = computed(() => {
  const onHoldClass = "bg-red-lighten-5 text-red-lighten-1 border-red-lighten-1";
  const notAvailableClass = "bg-grey-lighten-4 text-grey-darken-1 border-grey-darken-1";
  const isOwnerOnHold = props.truck.owner.ownerStatus === OwnerStatus.OnHold;
  const isDriverOneOnHold =
    props.truck.driver?.driverStatus && DriverStatusOnHold.includes(props.truck.driver.driverStatus);

  if (props.truck?.archived) {
    return notAvailableClass;
  }

  if (isOwnerOnHold || isDriverOneOnHold) {
    return onHoldClass;
  }

  switch (props.truck.status) {
    case TruckStatus.Available:
      return "bg-green-lighten-5 text-green border-green";
    case TruckStatus.InRoute:
      return "bg-indigo-lighten-5 text-indigo border-indigo";
    case TruckStatus.WillBeAvailable:
      return "bg-amber-lighten-5 text-amber-darken-3 border-amber-darken-3";
    case TruckStatus.NotAvailable:
      return notAvailableClass;
    case TruckStatus.OnHold:
      return onHoldClass;
    default:
      return notAvailableClass;
  }
});
</script>

<template>
  <advanced-marker v-if="position" :options="{ position }">
    <template #content>
      <div :class="statusClasses" class="truck-marker">
        <v-icon>mdi-truck</v-icon>
        <span class="font-weight-500">{{ truck.number }}</span>
      </div>
    </template>
  </advanced-marker>
</template>

<style lang="scss" scoped>
.truck-marker {
  position: relative;
  padding: 6px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  border-width: 2px;

  &::after {
    content: "";
    position: absolute;
    bottom: -10px;
    left: 20%;
    transform: translateX(-10%);
    width: 0;
    height: 0;
    border-left: 7px solid transparent;
    border-right: 7px solid transparent;
    border-top: 9px solid;
    border-top-color: inherit;
  }
}
</style>
